import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Work as WorkIcon,
  AccessTime as TimeIcon,
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
  Visibility as ObservationIcon,
  OpenInNew as OpenInNewIcon,
  Security as SafetyIcon,
  LocalShipping as TruckIcon,
  GpsFixed as GpsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  Pause as PauseIcon,
  Replay as ResumeIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { canViewCustomerPhone, hasMinRole } from '../../utils/roles';
import { crewSeatName } from '../../utils/truckCrewNames';
import InlineInsightChip from '../Insights/InlineInsightChip';
import useInsightCapabilities from '../Insights/useInsightCapabilities';
import WinterJobDetails from './WinterJobDetails';
import SummerJobDetails from './SummerJobDetails';
import ConstructionJobDetails from './ConstructionJobDetails';
import JobPhotosSection from './JobPhotosSection';
import PropertyDiagramViewer from '../Common/PropertyDiagramViewer';

// TKT-193: shared role gate. Messages / Observations / Safety tabs
// expose office-only information (PM communications, QA notes, safety
// docs) so they should not appear for crew/labour accounts.
const userIsOfficeOrAdmin = (user) =>
  user?.user_type === 'admin'
  || user?.user_type === 'superadmin'
  || user?.user_type === 'office'
  || user?.user_type === 'sales';

const JobDetailView = ({ jobId, onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showCustomerPhone = canViewCustomerPhone(user);
  const navigate = useNavigate();

  const handleOpenQuote = () => {
    if (!job?.quote_id) return;
    navigate(`/dashboard/quotes?quoteId=${encodeURIComponent(job.quote_id)}&action=view`);
  };
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [refreshingCustomer, setRefreshingCustomer] = useState(false);
  
  // Dialog states
  const [addMessageOpen, setAddMessageOpen] = useState(false);
  const [addObservationOpen, setAddObservationOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageOfficeOnly, setMessageOfficeOnly] = useState(false);
  const [observationData, setObservationData] = useState({
    observation_template_id: '',
    custom_note: '',
    severity: 'normal'
  });

  // Lookup data
  const [observationTemplates, setObservationTemplates] = useState([]);

  // Assign / change truck dialog state. Used for:
  // - One-off jobs with no truck (legacy imports, new construction, etc.)
  // - Reassigning any job to a different truck
  // - Correcting the scheduled_date on one-off jobs
  const [assignTruckOpen, setAssignTruckOpen] = useState(false);
  const [assignTruckSaving, setAssignTruckSaving] = useState(false);
  const [assignTruckError, setAssignTruckError] = useState('');
  const [trucksList, setTrucksList] = useState([]);
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [truckFormId, setTruckFormId] = useState('');
  const [dateFormValue, setDateFormValue] = useState('');

  // TKT-239: office-driven Mark Complete for construction jobs.
  const [markCompleteOpen, setMarkCompleteOpen] = useState(false);
  const [markCompleteSubmitting, setMarkCompleteSubmitting] = useState(false);

  // Inline AI hint: predicted visit duration for this job's site, from the
  // job-duration model. Only fetched for incomplete jobs when the
  // prediction_insights capability is on and the user can call the
  // ops-viewer prediction endpoints (crew_manager or admin+).
  const capabilities = useInsightCapabilities();
  const canSeePredictions = Boolean(capabilities.prediction_insights?.effective_enabled)
    && (user?.user_type === 'crew_manager' || hasMinRole(user, 'admin'));
  const [durationPrediction, setDurationPrediction] = useState(null);

  useEffect(() => {
    if (!canSeePredictions || !job?.id || job.is_complete) {
      setDurationPrediction(null);
      return;
    }
    let cancelled = false;
    makeApiCall(`api/insights/predictions/job-duration/${job.id}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!cancelled && payload?.prediction?.prediction) {
          setDurationPrediction(payload.prediction);
        }
      })
      .catch(() => {
        // Inline hint only — never surface errors for it.
      });
    return () => {
      cancelled = true;
    };
  }, [canSeePredictions, job?.id, job?.is_complete]);

  // ====================================
  // DATA FETCHING
  // ====================================

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await makeApiCall(`api/jobs/${jobId}`);
      
      if (response.ok) {
        const data = await response.json();
        setJob(data);
        
        // Fetch observation templates based on job type
        fetchObservationTemplates(data.job_type);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('jobs.failedToLoadJob'));
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      setError(t('jobs.failedToLoadJob'));
    } finally {
      setLoading(false);
    }
  };

  const fetchObservationTemplates = async (jobType) => {
    try {
      const response = await makeApiCall(`api/jobs/observation-templates?observation_type=${jobType}`);
      if (response.ok) {
        const data = await response.json();
        setObservationTemplates(data.observation_templates || []);
      }
    } catch (error) {
      console.error('Error fetching observation templates:', error);
    }
  };

  const refreshCustomerData = async () => {
    if (!job?.customer_id) return;
    
    try {
      setRefreshingCustomer(true);
      // Add cache-busting parameter to ensure fresh data
      const timestamp = Date.now();
      const response = await makeApiCall(`api/customers/${job.customer_id}?t=${timestamp}`);
      
      if (response.ok) {
        const customerData = await response.json();
        
        console.log('📋 Refreshed customer data:', customerData);
        console.log('📍 Address fields:', {
          street_address: customerData.street_address,
          city: customerData.city,
          province: customerData.province,
          postal_code: customerData.postal_code
        });
        
        // Update the job state with fresh customer data
        setJob(prevJob => {
          const updatedJob = {
            ...prevJob,
            first_name: customerData.first_name,
            last_name: customerData.last_name,
            company_name: customerData.company_name,
            phone_number: customerData.phone_number,
            email_address: customerData.email_address,
            street_address: customerData.street_address,
            city: customerData.city,
            province: customerData.province,
            postal_code: customerData.postal_code
          };
          
          console.log('✅ Updated job state:', updatedJob);
          return updatedJob;
        });
        
        setSuccess(t('jobs.customerDataRefreshed'));
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('jobs.failedToRefreshCustomer'));
      }
    } catch (error) {
      console.error('Error refreshing customer data:', error);
      setError(t('jobs.failedToRefreshCustomer'));
    } finally {
      setRefreshingCustomer(false);
    }
  };

  // ====================================
  // ASSIGN / CHANGE TRUCK
  // ====================================

  // Recurring jobs are anchored to a weekly route via job_site_id. The truck
  // can still be overridden per-visit (writes active_jobs.truck_id which
  // wins via COALESCE) but the date cannot - it's derived from the route's
  // day_of_week. One-offs have no job_site_id and can change both.
  const isRecurringJob = Boolean(job?.job_site_id);

  const loadTrucksForPicker = async () => {
    try {
      setTrucksLoading(true);
      const res = await makeApiCall('/api/routes/trucks');
      if (!res.ok) {
        throw new Error('Failed to load trucks');
      }
      const data = await res.json();
      const list = (data.trucks || []).filter(tr => tr.is_active !== false);
      setTrucksList(list);
    } catch (e) {
      console.error('Error loading trucks:', e);
      setTrucksList([]);
      setAssignTruckError(t('jobs.failedToLoadTrucks') || 'Failed to load trucks');
    } finally {
      setTrucksLoading(false);
    }
  };

  const openAssignTruckDialog = () => {
    setAssignTruckError('');
    setTruckFormId(job?.truck_id ? String(job.truck_id) : '');
    const iso = job?.scheduled_date
      ? new Date(job.scheduled_date).toISOString().split('T')[0]
      : '';
    setDateFormValue(iso);
    setAssignTruckOpen(true);
    loadTrucksForPicker();
  };

  const closeAssignTruckDialog = () => {
    if (assignTruckSaving) return;
    setAssignTruckOpen(false);
    setAssignTruckError('');
  };

  const handleSaveTruckAssignment = async () => {
    if (!job?.id) return;
    if (!truckFormId) {
      setAssignTruckError(t('jobs.truckRequired') || 'Please pick a truck');
      return;
    }
    setAssignTruckSaving(true);
    setAssignTruckError('');
    try {
      const body = { truck_id: parseInt(truckFormId, 10) };
      if (!isRecurringJob && dateFormValue) {
        body.scheduled_date = dateFormValue;
      }
      const res = await makeApiCall(`api/jobs/${job.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.errorKey === 'recurringDateBlocked') {
          throw new Error(t('jobs.recurringDateBlocked'));
        }
        throw new Error(data.error || t('jobs.truckAssignmentFailed'));
      }
      setAssignTruckOpen(false);
      setSuccess(t('jobs.truckAssignedSuccess'));
      await fetchJob();
    } catch (e) {
      console.error('Error assigning truck:', e);
      setAssignTruckError(e.message || t('jobs.truckAssignmentFailed'));
    } finally {
      setAssignTruckSaving(false);
    }
  };

  // ====================================
  // JOB ACTIONS
  // ====================================
  //
  // The "Start Job" button in the header now calls handleClockIn
  // (POST /clock-in) instead of /start-job, because /clock-in does the
  // same DB work AND inserts the per-user audit row in
  // job_clock_records. The legacy /start-job endpoint is kept on the
  // backend for compatibility with older callers, but is no longer
  // used by this view.

  const handleCompleteJob = async () => {
    try {
      // Services checkboxes are saved incrementally via PATCH — don't
      // include services_performed here, or we'd wipe them.
      const response = await makeApiCall(`api/jobs/${jobId}/complete-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completion_notes: ''
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || 'Job completed successfully');
        fetchJob();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to complete job');
      }
    } catch (error) {
      console.error('Error completing job:', error);
      setError('Failed to complete job');
    }
  };

  // TKT-239: construction jobs are office-managed and frequently never
  // clocked in, so handleCompleteJob (which requires a clock-in) can't
  // complete them. This calls the office-driven /mark-complete endpoint.
  const handleMarkComplete = async () => {
    try {
      setMarkCompleteSubmitting(true);
      const response = await makeApiCall(`api/jobs/${jobId}/mark-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('jobs.markCompleteFailed'));
      }
      setSuccess(data.message || t('jobs.markCompleteSuccess'));
      setMarkCompleteOpen(false);
      fetchJob();
    } catch (error) {
      console.error('Error marking job complete:', error);
      setError(error.message || t('jobs.markCompleteFailed'));
    } finally {
      setMarkCompleteSubmitting(false);
    }
  };

  const handleClockIn = async () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const response = await makeApiCall(`api/jobs/${jobId}/clock-in`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              selected_services: [] // TODO: Add service selection
            })
          });

          if (response.ok) {
            const data = await response.json();
            setSuccess(data.message || t('jobs.clockedInSuccessfully'));
            fetchJob();
          } else {
            const errorData = await response.json();
            setError(errorData.error || t('jobs.failedToClockIn'));
          }
        });
      }
    } catch (error) {
      console.error('Error clocking in:', error);
      setError(t('jobs.failedToClockIn'));
    }
  };

  const handleClockOut = async () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const response = await makeApiCall(`api/jobs/${jobId}/clock-out`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              completion_notes: '',
              services_performed: [],
              quality_rating: 5,
              customer_satisfaction: 5
            })
          });

          if (response.ok) {
            const data = await response.json();
            setSuccess(data.message || t('jobs.clockedOutSuccessfully'));
            fetchJob();
          } else {
            const errorData = await response.json();
            setError(errorData.error || t('jobs.failedToClockOut'));
          }
        });
      }
    } catch (error) {
      console.error('Error clocking out:', error);
      setError(t('jobs.failedToClockOut'));
    }
  };

  const handleAddMessage = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${jobId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_content: messageText,
          message_type: 'standard',
          is_admin_only: messageOfficeOnly
        })
      });

      if (response.ok) {
        setSuccess(t('jobs.messageAddedSuccessfully'));
        setMessageText('');
        setMessageOfficeOnly(false);
        setAddMessageOpen(false);
        fetchJob();
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('jobs.failedToAddMessage'));
      }
    } catch (error) {
      console.error('Error adding message:', error);
      setError(t('jobs.failedToAddMessage'));
    }
  };

  const handleAddObservation = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${jobId}/observations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(observationData)
      });

      if (response.ok) {
        setSuccess(t('jobs.observationAddedSuccessfully'));
        setObservationData({
          observation_template_id: '',
          custom_note: '',
          severity: 'normal'
        });
        setAddObservationOpen(false);
        fetchJob();
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('jobs.failedToAddObservation'));
      }
    } catch (error) {
      console.error('Error adding observation:', error);
      setError(t('jobs.failedToAddObservation'));
    }
  };

  const handleResendCompletionEmail = async () => {
    try {
      const response = await makeApiCall(`api/jobs/${jobId}/resend-completion-email`, {
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.sent) {
        setSuccess(t('jobs.completionEmailSent'));
      } else {
        setError(data.reason || data.error || t('jobs.completionEmailFailed'));
      }
    } catch (err) {
      console.error('Resend completion email error:', err);
      setError(t('jobs.completionEmailFailed'));
    }
  };


  // ====================================
  // UTILITY FUNCTIONS
  // ====================================

  const getJobStatusColor = (job) => {
    if (job.is_complete) return 'success';
    if (job.clock_in_time && !job.clock_out_time) return 'warning';
    if (job.requires_approval) return 'error';
    return 'default';
  };

  const getJobStatusText = (job) => {
    if (job.is_complete) return t('jobs.completed');
    if (job.clock_in_time && !job.clock_out_time) return t('jobs.inProgress');
    if (job.requires_approval) return t('jobs.needsApproval');
    return t('jobs.scheduled');
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    return new Date(dateTime).toLocaleString();
  };

  const formatDuration = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return '';
    const duration = new Date(clockOut) - new Date(clockIn);
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getJobTypeColor = (type) => {
    switch (type) {
      case 'summer': return 'primary';
      case 'winter': return 'info';
      case 'construction': return 'warning';
      default: return 'default';
    }
  };

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  const renderJobHeader = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <WorkIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {job.job_number}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {job.description}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" gap={1}>
            <Chip
              label={getJobStatusText(job)}
              color={getJobStatusColor(job)}
            />
            <Chip
              label={job.job_type}
              color={getJobTypeColor(job.job_type)}
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
        </Box>

        {/* Job-state actions.
            The job is a 4-state machine — Scheduled / In Progress /
            On Break / Completed — and we surface exactly one primary
            action per state, plus a one-line helper under each button
            so admins and crews stop wondering whether "Clock In" and
            "Start Job" are different things, or whether they have to
            "Clock Out" before "Complete Job". They don't, and they don't.

            Add Message / Add Observation are intentionally NOT here —
            those buttons live inside the Messages / Observations tab
            panels below, where they're contextually relevant. Putting
            them in this header was redundant and threw off the
            alignment with the helper-text actions. */}
        {(() => {
          const currentUserAssignment = job.assigned_employees?.find(
            emp => emp.user_id === user?.id
          );
          // TKT-193: user object exposes user_type, not role. Same bug
          // as ConstructionJobDetails — fixing here unblocks admin-only
          // controls in the JobDetailView header.
          const isAdmin = user?.user_type === 'admin' || user?.user_type === 'superadmin';
          const isSupervisor =
            currentUserAssignment?.role === 'supervisor' ||
            currentUserAssignment?.role === 'driver';
          const canControlJob = isAdmin || isSupervisor;
          const isAssignedCrew = !!currentUserAssignment;

          // Has the current user got an open per-user audit row on
          // this job? Used to offer an "I'm Here" button to a passenger
          // who arrives after the driver already started the job.
          const userHasActiveClock = job.clock_records?.some(
            r => r.user_id === user?.id && !r.clock_out_time
          );

          const phase = job.is_complete
            ? 'completed'
            : !job.clock_in_time
              ? 'scheduled'
              : job.clock_out_time
                ? 'paused'
                : 'in_progress';

          if (phase === 'completed') return null;

          // Build the list of actions, then render in a uniform grid
          // so every button shares the same column width and helper
          // text wraps consistently underneath. This avoids the ragged
          // wrap heights we had before.
          const actions = [];

          if (phase === 'scheduled' && (canControlJob || isAssignedCrew)) {
            actions.push({
              key: 'start',
              label: t('jobs.startJob'),
              helper: t('jobs.startJobHelper'),
              onClick: handleClockIn,
              icon: <WorkIcon />,
              color: 'success',
              variant: 'contained',
              primary: true,
            });
          }
          if (phase === 'in_progress' && canControlJob) {
            actions.push({
              key: 'complete',
              label: t('jobs.completeJob'),
              helper: t('jobs.completeJobHelper'),
              onClick: handleCompleteJob,
              icon: <CompleteIcon />,
              color: 'success',
              variant: 'contained',
              primary: true,
            });
          }
          if (phase === 'in_progress' && (canControlJob || isAssignedCrew)) {
            actions.push({
              key: 'break',
              label: t('jobs.takeBreak'),
              helper: t('jobs.takeBreakHelper'),
              onClick: handleClockOut,
              icon: <PauseIcon />,
              color: 'warning',
              variant: 'outlined',
            });
          }
          if (phase === 'in_progress' && isAssignedCrew && !userHasActiveClock) {
            actions.push({
              key: 'imhere',
              label: t('jobs.imHere'),
              helper: t('jobs.imHereHelper'),
              onClick: handleClockIn,
              icon: <ClockInIcon />,
              color: 'primary',
              variant: 'outlined',
            });
          }
          if (phase === 'paused' && (canControlJob || isAssignedCrew)) {
            actions.push({
              key: 'resume',
              label: t('jobs.resume'),
              helper: t('jobs.resumeHelper'),
              onClick: handleClockIn,
              icon: <ResumeIcon />,
              color: 'primary',
              variant: 'contained',
              primary: true,
            });
          }
          if (phase === 'paused' && canControlJob) {
            actions.push({
              key: 'complete-from-break',
              label: t('jobs.completeJob'),
              helper: t('jobs.completeJobHelper'),
              onClick: handleCompleteJob,
              icon: <CompleteIcon />,
              color: 'success',
              variant: 'contained',
            });
          }

          if (actions.length === 0) return null;

          return (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.default',
              }}
            >
              <Grid container spacing={2}>
                {actions.map((a) => (
                  <Grid
                    key={a.key}
                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Button
                      variant={a.variant}
                      color={a.color}
                      startIcon={a.icon}
                      onClick={a.onClick}
                      size={a.primary ? 'large' : 'medium'}
                      aria-label={a.label}
                      sx={{
                        fontWeight: a.primary ? 700 : 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        minWidth: 180,
                      }}
                    >
                      {a.label}
                    </Button>
                    {a.helper && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.75,
                          lineHeight: 1.35,
                          textAlign: 'left',
                        }}
                      >
                        {a.helper}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Paper>
          );
        })()}

        {/* TKT-239: office-driven Mark Complete for construction jobs.
            Kept separate from the crew state-machine actions above because
            those complete via clock-out (which requires a clock-in a
            construction job usually never had). Shown to office roles on
            any non-complete construction job. */}
        {job.job_type === 'construction' && !job.is_complete && !job.is_cancelled
          && userIsOfficeOrAdmin(user) && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CompleteIcon />}
              onClick={() => setMarkCompleteOpen(true)}
              aria-label={t('jobs.markComplete')}
              sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}
            >
              {t('jobs.markComplete')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {t('jobs.markCompleteHelper')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderJobInfo = () => (
    <Grid container spacing={3} mb={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {t('jobs.customerInformation')}
              </Typography>
              <Tooltip title={t('jobs.refreshCustomerData')}>
                <IconButton 
                  onClick={refreshCustomerData} 
                  disabled={refreshingCustomer}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ mr: 2 }}>
                {job.company_name ? <BusinessIcon /> : <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant="subtitle1">
                  {job.company_name || `${job.first_name} ${job.last_name}`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Customer ID: {job.customer_id}
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" mb={1}>
              <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                {job.street_address || job.city || job.province
                  ? `${job.street_address || ''}${job.street_address && (job.city || job.province) ? ', ' : ''}${job.city || ''}${job.city && job.province ? ', ' : ''}${job.province || ''}`
                  : 'No address'
                }
              </Typography>
            </Box>
            
            {showCustomerPhone && job.phone_number && (
              <Box display="flex" alignItems="center" mb={1}>
                <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{job.phone_number}</Typography>
              </Box>
            )}
            
            {job.email_address && (
              <Box display="flex" alignItems="center">
                <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{job.email_address}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('jobs.jobDetails')}
            </Typography>
            
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">{t('jobs.scheduledDate')}</Typography>
              <Typography variant="body1">
                {formatScheduledDate(job.scheduled_date)}
              </Typography>
              {durationPrediction && (
                <Box sx={{ mt: 0.75 }}>
                  <InlineInsightChip
                    label={t('insights.inline.durationChip', {
                      minutes: Math.round(Number(durationPrediction.prediction)),
                    })}
                    tone="info"
                    ariaLabel={t('insights.inline.durationAria', {
                      minutes: Math.round(Number(durationPrediction.prediction)),
                    })}
                    title={t('insights.inline.durationTitle')}
                  >
                    <Typography variant="body2">
                      {t('insights.inline.durationRange', {
                        lower: Math.round(Number(
                          durationPrediction.uncertainty?.lower_80
                          ?? durationPrediction.prediction
                        )),
                        upper: Math.round(Number(
                          durationPrediction.uncertainty?.upper_80
                          ?? durationPrediction.prediction
                        )),
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {durationPrediction.source_type === 'predictive_model'
                        ? t('insights.inline.durationBasisModel')
                        : t('insights.inline.durationBasisHistory')}
                    </Typography>
                  </InlineInsightChip>
                </Box>
              )}
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">{t('jobs.assignedTruck')}</Typography>
              {job.truck_name ? (
                <>
                  {/* Truck row laid out as label-on-top / value-below
                      to match the rhythm of the surrounding rows. The
                      "Change Truck" link sits as a subtle text button
                      right after the value so the column doesn't grow
                      into a multi-line block. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                    <TruckIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {job.truck_name}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={openAssignTruckDialog}
                      aria-label={t('jobs.changeTruck')}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('jobs.changeTruck')}
                    </Button>
                  </Box>
                </>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TruckIcon />}
                  onClick={openAssignTruckDialog}
                  aria-label={t('jobs.assignTruck')}
                  sx={{ mt: 0.5 }}
                >
                  {t('jobs.assignTruck')}
                </Button>
              )}
            </Box>
            
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">{t('jobs.timeTracking')}</Typography>
              <Typography variant="body1">
                Clock In: {formatDateTime(job.clock_in_time)}
              </Typography>
              <Typography variant="body1">
                Clock Out: {formatDateTime(job.clock_out_time)}
              </Typography>
              {job.clock_in_time && job.clock_out_time && (
                <Typography variant="body1" color="primary.main" fontWeight="bold">
                  Duration: {formatDuration(job.clock_in_time, job.clock_out_time)}
                </Typography>
              )}
              {job.is_complete && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleResendCompletionEmail}
                    aria-label={t('jobs.resendCompletionEmail')}
                  >
                    {t('jobs.resendCompletionEmail')}
                  </Button>
                  {job.completion_email_sent_at && (
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                      {t('jobs.completionEmailSentAt', {
                        time: new Date(job.completion_email_sent_at).toLocaleString(),
                      })}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
            {job.quote_number && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {t('jobs.relatedQuote')}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenQuote}
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  disabled={!job.quote_id}
                  aria-label={t('jobs.viewQuote', { quoteNumber: job.quote_number })}
                  sx={{ px: 0, minWidth: 0, fontWeight: 600 }}
                >
                  #{job.quote_number}
                </Button>
              </Box>
            )}

            {/* Property/site diagram captured on the originating quote.
                Shown small here; tapping opens a full-screen pan/zoom view. */}
            {job.site_diagram_signed_url && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t('jobs.propertyDiagram')}
                </Typography>
                <PropertyDiagramViewer
                  signedUrl={job.site_diagram_signed_url}
                  filename={job.site_diagram_filename}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Crew is a property of the truck (legacy parity) — not of individual jobs.
  // Show the truck's current driver / passenger and link to Route Planning
  // where they can be changed.
  const renderAssignedCrew = () => {
    const driverName = job.driver_first_name || job.driver_last_name
      ? `${job.driver_first_name || ''} ${job.driver_last_name || ''}`.trim()
      : null;
    const passengerName = job.passenger_first_name || job.passenger_last_name
      ? `${job.passenger_first_name || ''} ${job.passenger_last_name || ''}`.trim()
      : null;

    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('jobs.crew')}
        </Typography>
        {!job.truck_id ? (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<TruckIcon fontSize="small" />}
                onClick={openAssignTruckDialog}
                aria-label={t('jobs.assignTruck')}
              >
                {t('jobs.assignTruck')}
              </Button>
            }
          >
            {t('jobs.noTruckAssignedYet')}
          </Alert>
        ) : (
          <>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {driverName || <em>{t('jobs.noDriverAssigned')}</em>}
                      <Chip label={t('jobs.driver')} size="small" color="primary" sx={{ height: 20 }} />
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {passengerName || <em>{t('jobs.noPassengerAssigned')}</em>}
                      <Chip label={t('jobs.passenger')} size="small" sx={{ height: 20 }} />
                    </Box>
                  }
                />
              </ListItem>
            </List>
            <Alert severity="info" icon={false} sx={{ mt: 1 }}>
              {t('jobs.crewFromTruckHelp', { truckName: job.truck_name || '' })}
            </Alert>
          </>
        )}
      </Box>
    );
  };

  const renderMessages = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t('jobs.jobMessages')}</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setAddMessageOpen(true)}
          size="small"
        >
          {t('jobs.addMessage')}
        </Button>
      </Box>
      
      <List>
        {job.messages && job.messages.length > 0 ? (
          job.messages.map((message) => (
            <ListItem key={message.id} divider>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2">
                      {message.sender_name || message.sender_username}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(message.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {message.message_content}
                    </Typography>
                    {message.is_priority && (
                      <Chip label={t('jobs.priority')} color="error" size="small" sx={{ mt: 1 }} />
                    )}
                    {message.is_admin_only && (
                      <Chip label={t('jobs.adminOnly')} color="warning" size="small" sx={{ mt: 1, ml: 1 }} />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText 
              primary={t('jobs.noMessagesYet')}
              secondary={t('jobs.addMessagesToCommunicate')}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  const renderObservations = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t('jobs.qualityObservations')}</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setAddObservationOpen(true)}
          size="small"
        >
          {t('jobs.addObservation')}
        </Button>
      </Box>
      
      <List>
        {job.observations && job.observations.length > 0 ? (
          job.observations.map((observation) => (
            <ListItem key={observation.id} divider>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2">
                      {observation.note_name || t('jobs.customNoteLabel')}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip 
                        label={t(`jobs.${observation.severity}`)} 
                        color={
                          observation.severity === 'critical' ? 'error'
                          : observation.severity === 'high' ? 'error'
                          : observation.severity === 'normal' ? 'primary'
                          : 'default'
                        }
                        size="small"
                      />
                      {observation.is_resolved && (
                        <Chip label={t('jobs.resolved')} color="success" size="small" />
                      )}
                    </Box>
                  </Box>
                }
                secondary={
                  <Box>
                    {observation.custom_note && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {observation.custom_note}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      {t('jobs.notedBy')} {observation.noted_by_username} {t('jobs.on')} {new Date(observation.noted_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText 
              primary={t('jobs.noObservationsYet')}
              secondary={t('jobs.addQualityObservations')}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  const renderSafetyDocuments = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('jobs.safetyDocuments')}
      </Typography>
      
      <List>
        {job.safety_documents && job.safety_documents.length > 0 ? (
          job.safety_documents.map((doc) => (
            <ListItem key={doc.id}>
              <ListItemIcon>
                <SafetyIcon />
              </ListItemIcon>
              <ListItemText
                primary={doc.document_name}
                secondary={`${doc.document_category} - ${new Date(doc.uploaded_at).toLocaleDateString()}`}
              />
            </ListItem>
          ))
        ) : (
          <ListItem>
            <ListItemText 
              primary={t('jobs.safetyDocuments')}
              secondary={t('jobs.addQualityObservations')}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  // ====================================
  // MAIN RENDER
  // ====================================

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>{t('jobs.loadingJobDetails')}</Typography>
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">{t('jobs.jobNotFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Job Header */}
      {renderJobHeader()}

      {/* Job Information */}
      {renderJobInfo()}

      {/* Tabbed Content. TKT-193: Messages/Observations/Safety hidden
          from labour accounts; they are office-only views. Tab indices
          rebuilt dynamically so the active tab stays valid for whichever
          set of tabs the current user can see. */}
      {(() => {
        const officeView = userIsOfficeOrAdmin(user);
        const tabSpecs = [
          {
            key: 'crew',
            label: t('jobs.crew'),
            icon: <PeopleIcon sx={{ mr: 1 }} />,
            badge: job.assigned_employees?.length || 0,
            render: renderAssignedCrew,
          },
          // Photos visible to everyone (crews take them in the field, the
          // office reviews them). Delete is office/admin-only in the UI.
          {
            key: 'photos',
            label: t('jobs.photos'),
            icon: <PhotoCameraIcon sx={{ mr: 1 }} />,
            badge: job.photos?.length || 0,
            render: () => (
              <JobPhotosSection
                jobId={job.id}
                canDelete={officeView}
                onChange={fetchJob}
              />
            ),
          },
        ];
        if (officeView) {
          tabSpecs.push(
            {
              key: 'messages',
              label: t('jobs.messages'),
              icon: <MessageIcon sx={{ mr: 1 }} />,
              badge: job.messages?.length || 0,
              render: renderMessages,
            },
            {
              key: 'observations',
              label: t('jobs.observations'),
              icon: <ObservationIcon sx={{ mr: 1 }} />,
              badge: job.observations?.length || 0,
              render: renderObservations,
            },
            {
              key: 'safety',
              label: t('jobs.safety'),
              icon: <SafetyIcon sx={{ mr: 1 }} />,
              badge: job.safety_documents?.length || 0,
              render: renderSafetyDocuments,
            }
          );
        }
        const safeIndex = Math.min(activeTab, tabSpecs.length - 1);
        return (
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={safeIndex} onChange={(e, value) => setActiveTab(value)}>
                {tabSpecs.map((spec) => (
                  <Tab
                    key={spec.key}
                    label={
                      <Badge badgeContent={spec.badge} color="primary">
                        <Box display="flex" alignItems="center">
                          {spec.icon}
                          {spec.label}
                        </Box>
                      </Badge>
                    }
                  />
                ))}
              </Tabs>
            </Box>
            <CardContent>
              {tabSpecs[safeIndex]?.render()}
            </CardContent>
          </Card>
        );
      })()}

      {/* Service-specific detail blocks.
          A single job can carry multiple service types (e.g. a quote with
          BOTH summer maintenance + snow), so we don't use job_type as an
          exclusive gate. Instead we look at what's in available_services
          and render every detail block that applies. job_type still seeds
          the default tab focus / header labelling elsewhere. */}
      {(() => {
        const svc = job.available_services || {};
        // Backend may send this as a JSON string; normalise to object.
        const services = (typeof svc === 'string')
          ? (() => { try { return JSON.parse(svc) || {}; } catch (_) { return {}; } })()
          : svc;
        // A winter visit shows only the winter log and a summer visit only
        // the summer log, even when the contract carries both (TKT-424/467).
        const isWinterJob = job.job_type === 'winter';
        const isSummerJob = job.job_type === 'summer';
        const hasSnow = isWinterJob || (!isSummerJob && !!services.snow);
        const hasSummer = isSummerJob || (!isWinterJob && Object.keys(services).some(
          (k) => k !== 'snow' && k !== 'construction'
        ));
        const hasConstruction = !!services.construction || job.job_type === 'construction';
        // Fallback for jobs created before available_services was populated:
        // fall back to job_type so nothing regresses.
        const fallbackOnly = !hasSnow && !hasSummer && !hasConstruction;
        return (
          <>
            {(hasSnow || (fallbackOnly && job.job_type === 'winter')) && (
              <WinterJobDetails job={job} onUpdate={fetchJob} />
            )}
            {(hasSummer || (fallbackOnly && job.job_type === 'summer')) && (
              <SummerJobDetails job={job} onUpdate={fetchJob} />
            )}
            {(hasConstruction || (fallbackOnly && job.job_type === 'construction')) && (
              <ConstructionJobDetails job={job} onUpdate={fetchJob} />
            )}
          </>
        );
      })()}

      {/* Add Message Dialog */}
      <Dialog open={addMessageOpen} onClose={() => setAddMessageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('jobs.addJobMessage')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('jobs.messages')}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            sx={{ mt: 1 }}
          />
          {/* TKT-369: office-only notes are hidden from Crew Mode. */}
          {hasMinRole(user, 'sales') && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={messageOfficeOnly}
                  onChange={(e) => setMessageOfficeOnly(e.target.checked)}
                />
              )}
              label={t('jobs.officeOnlyNoteOption')}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMessageOpen(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleAddMessage} 
            variant="contained"
            disabled={!messageText.trim()}
          >
            {t('jobs.addMessage')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Observation Dialog */}
      <Dialog open={addObservationOpen} onClose={() => setAddObservationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('jobs.addQualityObservation')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t('jobs.selectTemplate')}</InputLabel>
                <Select
                  value={observationData.observation_template_id}
                  label={t('jobs.selectTemplate')}
                  onChange={(e) => setObservationData(prev => ({ 
                    ...prev, 
                    observation_template_id: e.target.value 
                  }))}
                >
                  <MenuItem value="">{t('jobs.customNote')}</MenuItem>
                  {observationTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.note_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('jobs.customNote')}
                value={observationData.custom_note}
                onChange={(e) => setObservationData(prev => ({ 
                  ...prev, 
                  custom_note: e.target.value 
                }))}
                placeholder={t('jobs.addQualityObservations')}
              />
            </Grid>
            
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t('jobs.severity')}</InputLabel>
                <Select
                  value={observationData.severity}
                  label={t('jobs.severity')}
                  onChange={(e) => setObservationData(prev => ({ 
                    ...prev, 
                    severity: e.target.value 
                  }))}
                >
                  <MenuItem value="low">{t('jobs.low')}</MenuItem>
                  <MenuItem value="normal">{t('jobs.normal')}</MenuItem>
                  <MenuItem value="high">{t('jobs.high')}</MenuItem>
                  <MenuItem value="critical">{t('jobs.critical')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddObservationOpen(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleAddObservation} 
            variant="contained"
            disabled={!observationData.observation_template_id && !observationData.custom_note}
          >
            {t('jobs.addObservation')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign / Change Truck Dialog */}
      <Dialog
        open={assignTruckOpen}
        onClose={closeAssignTruckDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="assign-truck-dialog-title"
      >
        <DialogTitle id="assign-truck-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TruckIcon color="primary" />
            {job?.truck_id ? t('jobs.changeTruck') : t('jobs.assignTruck')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {assignTruckError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignTruckError('')}>
                {assignTruckError}
              </Alert>
            )}

            {isRecurringJob && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('jobs.recurringTruckNote')}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }} disabled={trucksLoading || assignTruckSaving}>
              <InputLabel id="assign-truck-select-label">{t('jobs.truckLabel')}</InputLabel>
              <Select
                labelId="assign-truck-select-label"
                label={t('jobs.truckLabel')}
                value={truckFormId}
                onChange={(e) => setTruckFormId(e.target.value)}
                inputProps={{ 'aria-label': t('jobs.truckLabel') }}
              >
                {trucksList.length === 0 && !trucksLoading && (
                  <MenuItem value="" disabled>
                    <em>{t('jobs.noTrucksAvailable')}</em>
                  </MenuItem>
                )}
                {trucksList.map((tr) => {
                  const driverName = crewSeatName(tr, 'driver') || '';
                  return (
                    <MenuItem key={tr.id} value={String(tr.id)}>
                      {tr.name}{driverName ? ` — ${driverName}` : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {!isRecurringJob && (
              <TextField
                fullWidth
                type="date"
                label={t('jobs.scheduledDateLabel')}
                value={dateFormValue}
                onChange={(e) => setDateFormValue(e.target.value)}
                disabled={assignTruckSaving}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': t('jobs.scheduledDateLabel') }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeAssignTruckDialog}
            disabled={assignTruckSaving}
            aria-label={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveTruckAssignment}
            variant="contained"
            color="primary"
            disabled={assignTruckSaving || trucksLoading || !truckFormId}
            startIcon={assignTruckSaving ? <CircularProgress size={18} /> : <TruckIcon />}
            aria-label={t('common.save')}
          >
            {assignTruckSaving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TKT-239: Mark Complete confirmation modal (no browser confirm). */}
      <Dialog
        open={markCompleteOpen}
        onClose={() => !markCompleteSubmitting && setMarkCompleteOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="detail-mark-complete-dialog-title"
      >
        <DialogTitle id="detail-mark-complete-dialog-title">{t('jobs.markComplete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('jobs.markCompleteConfirm', {
              // TKT-239: fall back to the customer name when job_number is
              // blank (common for construction jobs).
              jobNumber: job?.job_number
                || job?.company_name
                || `${job?.first_name || ''} ${job?.last_name || ''}`.trim()
                || t('jobs.thisJob')
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkCompleteOpen(false)}
            disabled={markCompleteSubmitting}
            aria-label={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkComplete}
            disabled={markCompleteSubmitting}
            startIcon={markCompleteSubmitting ? <CircularProgress size={18} /> : <CompleteIcon />}
            aria-label={t('jobs.markComplete')}
          >
            {markCompleteSubmitting ? t('common.loading') : t('jobs.markComplete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert onClose={() => setSuccess('')} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobDetailView;
